import { useState, type FormEvent } from "react";
import { firmsApi, usersApi, type Role } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad, errText } from "../ui/useLoad";
import { formKeys } from "../ui/form";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { useSession } from "../auth/session";

const ROLES: [Role, string][] = [
  ["owner", "Everything, including users, owners and archiving"],
  ["admin", "Everything except owners and archiving"],
  ["accountant", "Enter, post, void; masters; GST / TDS; banking; import; year close"],
  ["clerk", "Enter drafts and masters; read"],
  ["viewer", "Read only"],
];

// Who can use this company. Owners and admins add people by the email of their Altekore account.
export function Users() {
  const { company } = useCompany();
  const { session } = useSession();
  const me = session?.user.id;
  const { data, error, loading, reload } = useLoad(() => usersApi.list(company.id), [company.id]);
  const manage = company.role === "owner" || company.role === "admin";
  const [email, setEmail] = useState(""); const [name, setName] = useState(""); const [role, setRole] = useState<Role>("accountant");
  const [err, setErr] = useState<string | null>(null); const [msg, setMsg] = useState<string | null>(null); const [busy, setBusy] = useState(false);
  const [inviteErr, setInviteErr] = useState<string | null>(null);

  const act = async (f: () => Promise<unknown>, ok: string) => {
    setBusy(true); setErr(null); setMsg(null);
    try { await f(); await reload(); setMsg(ok); } catch (x) { setErr(errText(x)); } finally { setBusy(false); }
  };
  // the form's own problem, said under it with the field outlined (not at the top of the page)
  const invite = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null); setMsg(null); setInviteErr(null);
    try {
      await usersApi.invite(company.id, email.trim(), role, name.trim() || undefined);
      setMsg(`Added ${email.trim()} as ${role}.`); setEmail(""); setName(""); await reload();
    } catch (x) { setInviteErr(errText(x)); } finally { setBusy(false); }
  };
  const emailBad = !!inviteErr && /account|email/i.test(inviteErr);
  const roleChoices = ROLES.filter(([r]) => r !== "owner" || company.role === "owner");

  return (
    <section className="page">
      <PageHead crumb="Company" title="People and roles" />
      <ErrorNote text={error?.message ?? err} />
      {msg && <p className="msg ok" role="status">{msg}</p>}
      {loading && !data ? <Loading /> : (
        <table className="grid-table">
          <thead><tr><th>User</th><th>Role</th><th></th></tr></thead>
          <tbody>
            {(data ?? []).map((u) => (
              <tr key={u.user_id} className={u.active ? "" : "muted"}>
                <td>{u.name ? <>{u.name} <span className="muted small">{u.email}</span></> : u.email}{u.user_id === me && <span className="tag">you</span>}{!u.active && <span className="tag">removed</span>}</td>
                <td>{manage && u.active && (u.role !== "owner" || company.role === "owner") ? (
                  <select value={u.role} disabled={busy} aria-label={`Role of ${u.email}`}
                    onChange={(e) => void act(() => usersApi.setRole(company.id, u.user_id, e.target.value as Role), `${u.email} is now ${e.target.value}.`)}>
                    {roleChoices.map(([r]) => <option key={r} value={r}>{r}</option>)}
                    {u.role === "owner" && company.role !== "owner" && <option value="owner">owner</option>}
                  </select>) : u.role}</td>
                <td>{manage && u.active && u.user_id !== me && (u.role !== "owner" || company.role === "owner") &&
                  <button className="link danger-link" disabled={busy} onClick={() => void act(() => usersApi.remove(company.id, u.user_id), `Removed ${u.email}.`)}>Remove</button>}
                  {!u.active && manage && <button className="link" disabled={busy} onClick={() => void act(() => usersApi.invite(company.id, u.email, u.role, u.name ?? undefined), `${u.email} is back.`)}>Add back</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {manage && (
        <form className="card panel invite" onSubmit={(e) => void invite(e)} onKeyDown={formKeys}>
          <h2>Add someone</h2>
          <p className="muted small">They need an Altekore account; add them by its email.</p>
          <div className="fields two">
            <label className={emailBad ? "bad" : ""}>Email<input type="email" required value={email} aria-invalid={emailBad || undefined}
              onChange={(e) => { setEmail(e.target.value); setInviteErr(null); }} /></label>
            <label>Name <span className="muted small">(optional)</span><input value={name} onChange={(e) => setName(e.target.value)} /></label>
            <label>Role<select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {roleChoices.map(([r, what]) => <option key={r} value={r}>{r} — {what}</option>)}</select></label>
          </div>
          <ErrorNote text={inviteErr} />
          <div className="row"><button className="primary" disabled={busy || !email.trim()}>Add</button></div>
        </form>
      )}
      <FirmLinks manage={manage} owner={company.role === "owner"} />
      <details className="roles"><summary>What each role can do</summary>
        <table className="grid-table compact"><tbody>{ROLES.map(([r, w]) => <tr key={r}><td>{r}</td><td>{w}</td></tr>)}</tbody></table>
      </details>
    </section>
  );
}

// accounting firms working on this company: their people get the role given here
function FirmLinks({ manage, owner }: { manage: boolean; owner: boolean }) {
  const { company } = useCompany();
  const [rev, setRev] = useState(0);
  const firms = useLoad(() => firmsApi.ofCompany(company.id), [company.id, rev]);
  const [code, setCode] = useState(""); const [role, setRole] = useState("accountant"); const [err, setErr] = useState<string | null>(null);
  const run = (p: Promise<unknown>) => { setErr(null); return p.then(() => { setRev(rev + 1); setCode(""); }).catch((e) => setErr(errText(e))); };
  const codeBad = !!err && /code/i.test(err);
  return (
    <section className="card panel invite">
      <h2>Accounting firms</h2>
      {firms.data?.length ? <table className="grid-table compact"><tbody>{firms.data.map((f) => <tr key={f.firm_id}><td>{f.name}</td><td>{f.role}</td><td className="muted small">{f.people} people</td>
        <td>{manage && <button className="link danger-link" onClick={() => void run(firmsApi.unlink(company.id, f.firm_id))}>Unlink</button>}</td></tr>)}</tbody></table>
        : <p className="muted small">No firm is linked. A firm gives you its code; its people then work in this company with the role you choose.</p>}
      {manage && <form className="fields inline" onSubmit={(e) => { e.preventDefault(); void run(firmsApi.link(company.id, code, role)); }}>
        <label className={codeBad ? "bad" : ""}>Firm code<input className="mono" maxLength={8} value={code} aria-invalid={codeBad || undefined}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(null); }} required /></label>
        <label>Their role<select value={role} onChange={(e) => setRole(e.target.value)}>{["admin", "accountant", "clerk", "viewer"].filter((r) => owner || r !== "admin").map((r) => <option key={r}>{r}</option>)}</select></label>
        <button>Link firm</button></form>}
      <ErrorNote text={err} />
    </section>
  );
}
